package fr.manyverse.app.domain.usecase

import fr.manyverse.app.domain.error.ValidationError
import fr.manyverse.app.domain.image.ImageTarget
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.CharacterToCreate
import fr.manyverse.app.domain.model.InventoryItemToCreate
import fr.manyverse.app.domain.repository.ImageStorageRepository
import fr.manyverse.app.domain.repository.InventoryRepository
import fr.manyverse.app.domain.repository.PreMadeCharacterRepository

/**
 * Crée le personnage d'un joueur à partir d'un pré-tiré : identité, fiche et objets de départ.
 *
 * Fiche et objets sont copiés en tout ou rien : s'ils manquaient, le joueur jouerait un personnage
 * incomplet sans le savoir. En cas d'échec, le personnage créé est supprimé, pour que le joueur
 * puisse réessayer sans doublon. Les images (avatar, objets) restent copiées au mieux : leur
 * absence se voit et se corrige.
 */
class CopyPreMadeToCharacterUseCase(
    private val preMadeCharacterRepository: PreMadeCharacterRepository,
    private val imageStorageRepository: ImageStorageRepository,
    private val createCharacterUseCase: CreateCharacterUseCase,
    private val updateCharacterUseCase: UpdateCharacterUseCase,
    private val updateCharacterSheetUseCase: UpdateCharacterSheetUseCase,
    private val createInventoryItemUseCase: CreateInventoryItemUseCase,
    private val inventoryRepository: InventoryRepository,
    private val deleteCharacterUseCase: DeleteCharacterUseCase
) {
    suspend operator fun invoke(
        preMadeId: String,
        userId: String,
        universeId: String
    ): Result<Character> {
        val preMade = preMadeCharacterRepository.getPreMadeCharacterById(preMadeId)
            .getOrElse { return Result.failure(it) }
            ?: return Result.failure(ValidationError("Pre-made character not found"))

        val created = createCharacterUseCase(
            universeId,
            CharacterToCreate(
                userId = userId,
                name = preMade.name,
                story = preMade.story
            )
        ).getOrElse { return Result.failure(it) }

        var finalCharacter = created

        if (preMade.imageUrl != null) {
            val downloaded = imageStorageRepository.downloadImage(preMade.imageUrl).getOrNull()
            if (downloaded != null) {
                val newPath = imageStorageRepository
                    .upload(ImageTarget.CharacterAvatar(userId, created.id), downloaded)
                    .getOrNull()
                if (newPath != null) {
                    finalCharacter = updateCharacterUseCase(
                        characterId = created.id,
                        name = created.name,
                        story = created.story,
                        imageUrl = newPath
                    ).getOrElse { finalCharacter }
                }
            }
        }

        val sheet = preMade.sheet
        val hasSheet = sheet.gauges.isNotEmpty() ||
            sheet.characteristics.isNotEmpty() ||
            sheet.skills.isNotEmpty() ||
            sheet.powers.isNotEmpty()

        if (hasSheet) {
            finalCharacter = updateCharacterSheetUseCase(finalCharacter.id, sheet)
                .getOrElse { return rollback(created, it) }
        }

        // Recopie des objets de départ dans l'inventaire réel du personnage
        preMade.inventory.forEach { item ->
            val createdItem = createInventoryItemUseCase(
                InventoryItemToCreate(
                    characterId = finalCharacter.id,
                    name = item.name,
                    category = item.category,
                    quantity = item.quantity
                )
            ).getOrElse { return rollback(created, it) }

            if (item.imageUrl != null) {
                val downloaded = imageStorageRepository.downloadImage(item.imageUrl).getOrNull()
                if (downloaded != null) {
                    val newPath = imageStorageRepository
                        .upload(ImageTarget.InventoryItem(finalCharacter.id, createdItem.id), downloaded)
                        .getOrNull()
                    if (newPath != null) {
                        inventoryRepository.updateImageUrl(createdItem.id, newPath)
                    }
                }
            }
        }

        return Result.success(finalCharacter)
    }

    /**
     * Supprime le personnage incomplet (ses objets suivent par cascade) et rend l'échec d'origine.
     * Si la suppression échoue aussi, le mapper l'a déjà remontée : le joueur garde un personnage
     * incomplet, mais il sait que la copie a échoué.
     */
    private suspend fun rollback(created: Character, failure: Throwable): Result<Character> {
        deleteCharacterUseCase(created.id)
        return Result.failure(failure)
    }
}
